import { addWeeks } from "date-fns";
import { useCallback } from "react";
import { AvailableService } from "@/components/booking/multiProviderAppointmentDrawer/appointmentServicesSection";
import { AppointmentServicesFragment } from "@/graphql/fragments/appointment/appointmentBasicInfo.graphql.types";
import { AppointmentServiceProviderBasicInfoFragment } from "@/graphql/queries/appointment/appointmentServiceProviderBasicInfo.graphql.types";
import useErrorLogger from "@/utils/useErrorLogger";
import useMedspaTimezone from "../common/useMedspaTimezone";

const WEEKS_TO_ADD_FOR_REBOOKING = 12;

export const useAppointmentServicesToFormInput = () => {
  const { utcToMedspaZonedTime } = useMedspaTimezone();
  const logError = useErrorLogger();

  const mapAppointmentServicesToFormInput = useCallback(
    (options: {
      appointmentServices: AppointmentServicesFragment["appointmentServices"];
      availableServices: AvailableService[];
      availableProviders: AppointmentServiceProviderBasicInfoFragment["provider"][];
      isRebooking?: boolean;
    }) => {
      const {
        appointmentServices,
        availableServices,
        availableProviders,
        isRebooking,
      } = options;

      return appointmentServices
        .map(({ id, serviceMenuItemId, providerId, startTime, endTime }) => {
          const serviceMenuItem = availableServices.find(
            (s) => s.id === serviceMenuItemId
          );

          if (!serviceMenuItem) {
            logError(
              `Service menu item with id ${serviceMenuItemId} not found for appointment service ${id}`
            );

            return null;
          }

          const provider = availableProviders.find((p) => p.id === providerId);

          if (!provider) {
            logError(
              `Provider with id ${providerId} not found for appointment service ${id}`
            );

            return null;
          }

          const _startTime = utcToMedspaZonedTime(startTime);
          const _endTime = utcToMedspaZonedTime(endTime);

          return {
            id,
            serviceMenuItem,
            provider,
            startTime: isRebooking
              ? addWeeks(_startTime, WEEKS_TO_ADD_FOR_REBOOKING)
              : _startTime,
            endTime: isRebooking
              ? addWeeks(_endTime, WEEKS_TO_ADD_FOR_REBOOKING)
              : _endTime,
          };
        })
        .filter((s) => s !== null);
    },
    [logError, utcToMedspaZonedTime]
  );

  return { mapAppointmentServicesToFormInput };
};
